import { Badge, BadgeSize, BadgeVariant } from "shared/components";
import { CreditReport } from "types/landlord/reports";
import { formatToCurrency } from "shared/utils";
import { Row } from "views/applications-id-report/components/Row";
import { Section } from "views/applications-id-report/components/Section";
import { DataVendors } from "shared/types";

/** TransUnion creditor classification code for rental/leasing */
const RENTAL_CREDITOR_CLASSIFICATION_CODE = 9;
/** TransUnion industry code for rental/leasing collections */
const RENTAL_COLLECTION_INDUSTRY_CODE = "Z";
const RENTAL_CREDITOR_CLASSIFICATION_DESCRIPTION = "RENTAL/LEASING";

type TrdPartyCollectionDetailsProps = {
  collections?: CreditReport["collections"];
  isNoHit: boolean;
  vendor?: DataVendors;
};

const isRentalCollection = (
  collection: NonNullable<CreditReport["collections"]>[number],
  vendor?: DataVendors
) => {
  const isRentalClassification =
    collection.creditor_classification?.description?.toUpperCase() ===
      RENTAL_CREDITOR_CLASSIFICATION_DESCRIPTION ||
    Number(collection.creditor_classification?.code) ===
      RENTAL_CREDITOR_CLASSIFICATION_CODE;

  if (vendor === DataVendors.TransUnion)
    return (
      collection.industry_code?.toUpperCase() ===
        RENTAL_COLLECTION_INDUSTRY_CODE ||
      collection.creditor_classification?.description?.toUpperCase() ===
        RENTAL_CREDITOR_CLASSIFICATION_DESCRIPTION
    );

  return isRentalClassification;
};

export const TrdPartyCollectionDetails = ({
  collections,
  isNoHit,
  vendor
}: TrdPartyCollectionDetailsProps) => {
  if (isNoHit)
    return (
      <Section sectionTitle="Third party collection details">
        <p className="text-base font-medium text-content-300">
          No data available
        </p>
      </Section>
    );

  return (
    <Section sectionTitle="Third party collection details">
      {collections?.length ? (
        <>
          {collections.map((collection, index) => (
            <div
              className="flex w-full gap-4"
              key={`credit-report-bankruptcy-${index}`}
            >
              <div className="w-full">
                <div className="h-7 w-full border-b border-alpha-40 flex gap-2 items-center">
                  <p className="test-sm font-bold text-content-1000">
                    Collection {index + 1}
                  </p>
                  {isRentalCollection(collection, vendor) && (
                    <Badge variant={BadgeVariant.WARNING} size={BadgeSize.SM}>
                      Rental
                    </Badge>
                  )}
                </div>
                <div className="mt-4 flex w-full mb-4">
                  <div className="flex w-full flex-col gap-3">
                    <Row
                      title="Balance"
                      value={
                        collection.balance
                          ? formatToCurrency(collection.balance)
                          : null
                      }
                    />
                    <Row
                      title="Industry Code"
                      value={collection.industry_code}
                    />
                    <Row
                      title="Customer Number"
                      value={collection.customer_number}
                    />
                    <Row
                      title="Account Number"
                      value={collection.account_number}
                    />
                    <Row title="Client Name" value={collection.client_name} />
                    <Row
                      title="Narrative Codes"
                      value={collection.narrative_codes
                        ?.map((nc) => nc.code)
                        .join(",")}
                    />
                    <Row
                      title="Status"
                      value={collection.status?.description}
                    />
                    <Row
                      title="Date Reported"
                      value={collection.date_reported}
                    />
                    <Row
                      title="Original Amount"
                      value={
                        collection.original_amount
                          ? formatToCurrency(collection.original_amount)
                          : null
                      }
                    />
                  </div>
                  <div className="flex w-full flex-col gap-3">
                    <Row
                      title="Creditor classification code"
                      value={collection.creditor_classification?.code}
                    />
                    <Row
                      title="Creditor classification description"
                      value={collection.creditor_classification?.description}
                    />
                    <Row
                      title="Account designator code"
                      value={collection.account_designator?.code}
                    />
                    <Row
                      title="Account designator description"
                      value={collection.account_designator?.description}
                    />
                  </div>
                </div>
              </div>
            </div>
          ))}
        </>
      ) : (
        <div className="text-base font-medium">No data available</div>
      )}
    </Section>
  );
};
